import React, { useCallback, useEffect, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import axiosInstance from '../services/axiosInterceptor';
import Swal from 'sweetalert2';
import { HealthAlert, outcomeBadge, sourceLabel, stayText, thaiDate, timeOf } from './nurseOptions';

// ช่องข้อมูลสุขภาพ — คอลัมน์เดียวกับที่ครูประจำชั้นแก้ที่ advisor/StudentHealth.js
// (แหล่งความจริงเดียวคือ tb_student ไม่มีตาราง profile ซ้ำของห้องพยาบาล)
const HEALTH_FIELDS = [
  { key: 'st_drug',            label: 'การแพ้ยา' },
  { key: 'st_food',            label: 'การแพ้อาหาร' },
  { key: 'st_syndrome',        label: 'โรคประจำตัว' },
  { key: 'st_facingSyndrome',  label: 'วิธีแก้ไขเมื่อเจ็บป่วยด้วยโรคประจำตัว' },
  { key: 'st_everSyndrome',    label: 'โรคที่เคยเป็น' },
  { key: 'st_accidentHistory', label: 'อุบัติเหตุร้ายแรงและความผิดปกติอื่นๆ' },
];

const CHECKUP_RESULT = {
  normal: { label: 'ปกติ', variant: 'success' },
  watch:  { label: 'เฝ้าระวัง', variant: 'warning' },
  refer:  { label: 'ส่งต่อ', variant: 'danger' },
};

const DENTAL_LABEL = { normal: 'ช่องปากปกติ', caries: 'ฟันผุ', refer: 'ส่งต่อทันตกรรม' };

const THAI_MONTH = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

const bmiOf = (w, h) => {
  const weight = Number(w);
  const height = Number(h);
  if (!weight || !height) return null;
  return (weight / ((height / 100) ** 2)).toFixed(1);
};

export default function NurseStudentFile() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { stId } = useParams();
  const yearId = localStorage.getItem('yearId');

  const [data, setData] = useState(null);
  // วัคซีนกับผลตรวจสุขภาพอยู่คนละตารางกับการมาห้องพยาบาล จึงดึงแยก (เฟส 3)
  const [history, setHistory] = useState({ vaccines: [], checkups: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [health, setHealth] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(() => {
    if (!stId || !yearId) return;
    setLoading(true);
    axiosInstance.get(`/api/nurse/students/${stId}?yearId=${yearId}`)
      .then(res => {
        setData(res.data);
        setHealth(Object.fromEntries(HEALTH_FIELDS.map(f => [f.key, res.data.student[f.key] ?? ''])));
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));

    axiosInstance.get(`/api/nurse/student-history/${stId}`)
      .then(res => setHistory(res.data || { vaccines: [], checkups: [] }))
      .catch(() => setHistory({ vaccines: [], checkups: [] }));
  }, [stId, yearId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveHealth = () => {
    setSaving(true);
    axiosInstance.put('/api/updateStudentHealth', { st_id: stId, ...health })
      .then(() => {
        setEditing(false);
        fetchData();
        Swal.fire({ icon: 'success', title: 'บันทึกข้อมูลสุขภาพแล้ว', timer: 1400, showConfirmButton: false });
      })
      .catch(err => Swal.fire({ icon: 'error', title: 'ไม่สามารถบันทึกได้', text: err?.response?.data?.message || '' }))
      .finally(() => setSaving(false));
  };

  const student = data?.student;

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/nurse/dashboard">ห้องพยาบาล</Link></li>
              <li className="breadcrumb-item active">แฟ้มสุขภาพรายคน</li>
            </ol>
            <h4 className="main-title mb-0">{student?.st_name || 'แฟ้มสุขภาพ'}</h4>
            {student && (
              <div className="fs-xs text-secondary">
                {student.st_idnumber}
                {student.r_no ? ` · ม.${student.c_id}/${student.r_no}` : ''}
                {student.islamic_class ? ` · ศาสนา ${student.islamic_class}` : ''}
                {student.dorm_name ? ` · หอพัก ${student.dorm_name}` : ''}
              </div>
            )}
          </div>
          <Link to="/nurse/visit" className="btn btn-success mt-3 mt-md-0">
            <i className="ri-add-line me-1"></i>บันทึกการมาห้องพยาบาล
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : !student ? (
          <Card className="card-one"><Card.Body className="text-center text-secondary py-5">ไม่พบนักเรียน</Card.Body></Card>
        ) : (
          <Row className="g-3">
            <Col xl={5}>
              <Card className="card-one mb-3">
                <Card.Header className="d-flex align-items-center">
                  <Card.Title as="h6" className="mb-0">ข้อมูลสุขภาพ</Card.Title>
                  <Button variant="link" size="sm" className="p-0 ms-auto"
                    onClick={() => { setEditing(!editing); if (editing) fetchData(); }}>
                    {editing ? 'ยกเลิก' : 'แก้ไข'}
                  </Button>
                </Card.Header>
                <Card.Body>
                  {!editing ? (
                    <>
                      <HealthAlert student={student} />
                      <Table size="sm" className="mb-0 fs-sm">
                        <tbody>
                          {HEALTH_FIELDS.map(f => (
                            <tr key={f.key}>
                              <td className="text-secondary" style={{ width: '45%' }}>{f.label}</td>
                              <td>{student[f.key] || <span className="text-secondary">—</span>}</td>
                            </tr>
                          ))}
                          <tr>
                            <td className="text-secondary">เบอร์ผู้ปกครอง</td>
                            <td>{student.st_parentPhone || student.st_emergencyPhone || <span className="text-secondary">—</span>}</td>
                          </tr>
                          <tr>
                            <td className="text-secondary">ติดต่อฉุกเฉิน</td>
                            <td>
                              {[student.st_emergencyFirstName, student.st_emergencyLastName].filter(Boolean).join(' ') || '—'}
                              {student.st_emergencyPhone ? ` · ${student.st_emergencyPhone}` : ''}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </>
                  ) : (
                    <>
                      {HEALTH_FIELDS.map(f => (
                        <Form.Group className="mb-2" key={f.key}>
                          <Form.Label className="fw-semibold fs-xs mb-1">{f.label}</Form.Label>
                          <Form.Control size="sm" value={health[f.key] || ''}
                            onChange={e => setHealth({ ...health, [f.key]: e.target.value })} />
                        </Form.Group>
                      ))}
                      <div className="d-grid mt-3">
                        <Button onClick={saveHealth} disabled={saving}>
                          {saving ? <Spinner size="sm" animation="border" /> : 'บันทึกข้อมูลสุขภาพ'}
                        </Button>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>

              <Card className="card-one">
                <Card.Header>
                  <Card.Title as="h6" className="mb-0">น้ำหนัก / ส่วนสูง</Card.Title>
                </Card.Header>
                <Card.Body className="p-0">
                  {!data.bodymass.length ? (
                    <div className="text-center text-secondary py-4 fs-sm">
                      ยังไม่มีข้อมูล — ครูประจำชั้นเป็นผู้ชั่งน้ำหนักวัดส่วนสูงรายเดือน
                    </div>
                  ) : (
                    <Table className="mb-0 fs-sm">
                      <thead>
                        <tr><th>เดือน</th><th className="text-end">น้ำหนัก</th><th className="text-end">ส่วนสูง</th><th className="text-end">BMI</th></tr>
                      </thead>
                      <tbody>
                        {data.bodymass.map(b => (
                          <tr key={b.stbm_id}>
                            <td>{THAI_MONTH[b.stmb_month] || b.stmb_month}</td>
                            <td className="text-end">{b.stbm_weight} กก.</td>
                            <td className="text-end">{b.stbm_height} ซม.</td>
                            <td className="text-end">{bmiOf(b.stbm_weight, b.stbm_height) || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col xl={7}>
              <Card className="card-one mb-3">
                <Card.Header>
                  <Card.Title as="h6" className="mb-0">
                    ประวัติการมาห้องพยาบาล
                    <Badge bg="secondary" className="ms-2">{data.visits.length}</Badge>
                  </Card.Title>
                </Card.Header>
                <Card.Body className="p-0">
                  {!data.visits.length ? (
                    <div className="text-center text-secondary py-4 fs-sm">ยังไม่เคยมาห้องพยาบาล</div>
                  ) : (
                    <div className="table-responsive">
                      <Table hover className="mb-0 fs-sm">
                        <thead>
                          <tr>
                            <th>วันที่ / เวลา</th>
                            <th>อาการ</th>
                            <th>สัญญาณชีพ</th>
                            <th>การรักษา</th>
                            <th>ผลลัพธ์</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.visits.map(v => (
                            <tr key={v.nv_id}>
                              <td className="text-nowrap">
                                <div>{thaiDate(v.nv_date)}</div>
                                <div className="fs-xs text-secondary">
                                  {timeOf(v.nv_check_in)}
                                  {v.nv_check_out ? ` - ${timeOf(v.nv_check_out)}` : ' - ยังอยู่'}
                                </div>
                                <div className="fs-xs text-secondary">{stayText(v.nv_check_in, v.nv_check_out)} · {sourceLabel(v.nv_source)}</div>
                              </td>
                              <td>
                                {v.ns_name || '-'}
                                {v.nv_symptom_detail && <div className="fs-xs text-secondary">{v.nv_symptom_detail}</div>}
                              </td>
                              <td className="fs-xs">
                                {v.nv_temp ? <div>{v.nv_temp}°C</div> : null}
                                {v.nv_pulse ? <div>ชีพจร {v.nv_pulse}</div> : null}
                                {v.nv_bp_sys ? <div>{v.nv_bp_sys}/{v.nv_bp_dia}</div> : null}
                                {v.nv_spo2 ? <div>SpO₂ {v.nv_spo2}%</div> : null}
                              </td>
                              <td className="fs-xs">{v.nv_treatment || '-'}</td>
                              <td>
                                {outcomeBadge(v.nv_outcome)}
                                {v.nv_refer_place && <div className="fs-xs text-secondary">{v.nv_refer_place}</div>}
                                <div className="fs-xs text-secondary">{v.recorded_by_name}</div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>

              <Row className="g-3">
                <Col md={6}>
                  <Card className="card-one h-100">
                    <Card.Header>
                      <Card.Title as="h6" className="mb-0">
                        วัคซีน
                        <Badge bg="secondary" className="ms-2">{history.vaccines.length}</Badge>
                      </Card.Title>
                    </Card.Header>
                    <Card.Body className="p-0">
                      {!history.vaccines.length ? (
                        <div className="text-center text-secondary py-4 fs-sm">ยังไม่มีบันทึกวัคซีน</div>
                      ) : (
                        <Table className="mb-0 fs-sm">
                          <tbody>
                            {history.vaccines.map(v => (
                              <tr key={v.nvc_id}>
                                <td>
                                  {v.vac_name}
                                  <div className="fs-xs text-secondary">
                                    เข็มที่ {v.vac_dose || '-'}{v.vac_place ? ` · ${v.vac_place}` : ''}
                                  </div>
                                </td>
                                <td className="text-end fs-xs">{thaiDate(v.vac_date)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="card-one h-100">
                    <Card.Header>
                      <Card.Title as="h6" className="mb-0">
                        ผลตรวจสุขภาพประจำปี
                        <Badge bg="secondary" className="ms-2">{history.checkups.length}</Badge>
                      </Card.Title>
                    </Card.Header>
                    <Card.Body className="p-0">
                      {!history.checkups.length ? (
                        <div className="text-center text-secondary py-4 fs-sm">ยังไม่มีผลตรวจ</div>
                      ) : (
                        <Table className="mb-0 fs-sm">
                          <tbody>
                            {history.checkups.map(c => (
                              <tr key={c.nc_id}>
                                <td>
                                  <div className="fw-semibold">
                                    ปี {c.year_year} ครั้งที่ {c.nc_round}
                                    <Badge bg={CHECKUP_RESULT[c.nc_result]?.variant || 'secondary'} className="ms-2 fw-normal">
                                      {CHECKUP_RESULT[c.nc_result]?.label || c.nc_result}
                                    </Badge>
                                  </div>
                                  <div className="fs-xs text-secondary">
                                    {thaiDate(c.nc_date)}
                                    {c.nc_vision_l ? ` · สายตา ${c.nc_vision_l}/${c.nc_vision_r}` : ''}
                                    {c.nc_dental && c.nc_dental !== 'na' ? ` · ${DENTAL_LABEL[c.nc_dental]}` : ''}
                                    {c.nc_lice ? ' · พบเหา' : ''}
                                  </div>
                                  {c.nc_note && <div className="fs-xs">{c.nc_note}</div>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        )}
        <Footer />
      </div>
    </>
  );
}
